import { Box, Typography } from '@material-ui/core';
import Header from 'components/Header/Header';
import Icon from 'components/Icon/Icon';
import CartItem from 'features/cart/components/CartItem/CartItem';
import { useCartStore } from 'features/cart/store/cartSlice';
import { IItemCart, IOrderCart } from 'features/cart/types/CartTypes';
import { useStore } from 'features/home/store/homeSlice';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { isHolidaySurcharge, renderTableId } from 'submodules/OrderCore';
import { theme } from 'theme';
import useAnalyticsEventTracker from 'utils/analytics/useAnalyticsEventTracker';
import { EEventAction, EEventCategory } from 'utils/constants';
import { getOrderHistoryStorage, loadUrlHome } from 'utils/localStorage';
import {
  IHandleErrorProps,
  getKeyRestaurant,
  getOrderHistoryOnTime,
  handleError,
  handleRouter,
  hasLength,
  updateUrlHomeStore,
} from 'utils/restaurantFeatures';
import { shallow } from 'zustand/shallow';
import { HomePageType } from '../Home/HomePage';
import { useParams } from 'react-router-dom';

const OrderHistoryPage = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: none;
`;

const Content = styled(Box)`
  flex: 1;
  margin-bottom: 50px;
`;

const InfoRow = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  position: relative;
`;

const Label = styled(Typography)`
  width: 90px;
  font-size: 14px;
  line-height: 24px;
  color: ${theme.palette.text.secondary};
`;

const Info = styled(Typography)`
  flex: 5;
  font-size: 14px;
  line-height: 24px;
  color: ${theme.palette.grey[800]};
  font-weight: 500;
  padding-left: 10px;
`;

const TableName = styled(Box)`
  position: absolute;
  right: -16px;
  background: #333333;
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.04);
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  padding: 3px 10px;
`;

const TextTable = styled(Box)`
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  color: ${theme.palette.grey[100]};
`;

const CartContent = styled(Box)`
  margin-top: 20px;
`;

const SubTotal = styled(Box)`
  display: flex;
  margin-top: 30px;
  padding-top: 19px;
`;

const TextNormal = styled(Typography)`
  font-size: 16px;
  line-height: 24px;
  color: ${theme.palette.grey[800]};
`;
const TextNoOrder = styled(Typography)`
  font-size: 18px;
  line-height: 24px;
  color: ${theme.palette.grey[800]};
  text-align: center;
`;

const TextRed = styled(TextNormal)`
  font-size: 14px;
  color: red;
`;

const TextRedLarge = styled(TextNormal)`
  font-size: 18px;
  color: red;
`;

const TextPrice = styled(Typography)`
  flex: 1;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.palette.grey[900]};
  text-align: right;
`;

const TextPriceLarge = styled(Typography)`
  flex: 1;
  font-weight: 600;
  font-size: 24px;
  line-height: 24px;
  color: ${theme.palette.grey[900]};
  text-align: right;
`;

const TextFee = styled(TextPrice)`
  color: red;
`;

const TextFeeLarge = styled(TextPriceLarge)`
  color: red;
  font-size: 20px;
`;

const QRBox = styled(Box)`
  background-color: #f8f8fa;
  border-radius: 12px;
  text-align: center;
  padding: 38px 0;
  margin-top: 20px;
`;

const IconBack = styled(Icon)`
  color: ${theme.palette.grey[800]};
`;

export const BoxWrapperIconBack = styled(Box)`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

interface IOrderHistoryProps {
  isNewOrder?: boolean;
}

const OrderHistory = ({ isNewOrder }: IOrderHistoryProps) => {
  const history = useHistory();
  const eventTracker = useAnalyticsEventTracker();
  const { tableId, menuId } = useParams<HomePageType>();
  const { restaurant, isDineInTable, info, state, setUrlHome } = useStore(
    (state) => ({
      restaurant: state.homeState.restaurant,
      isDineInTable: state.homeState.isDineInTable,
      info: state.homeState.info,
      state: state.homeState,
      setUrlHome: state.setUrlHome,
    }),
    shallow,
  );

  const { removeItem } = useCartStore(
    (state) => ({
      removeItem: state.removeItem,
    }),
    shallow,
  );
  const keyRestaurant = getKeyRestaurant(loadUrlHome());
  const orderHistory = getOrderHistoryStorage(keyRestaurant);
  const newOrder = orderHistory?.items[0];
  const orderHistoryItemsOnTime = getOrderHistoryOnTime(orderHistory?.items || []);
  const holidaySurcharge = restaurant?.holidaySurcharge || {};
  const amountSurchargeFee = holidaySurcharge?.amount || 0;
  const { t } = useTranslation();

  const back = () => {
    handleRouter({
      history,
      state,
      customUrl: '',
      fileName: 'OrderHistory',
    });
  };

  const onRemoveItem = (index: number) => {
    removeItem({ index, info });
  };

  const renderSurchargeFee = (orderHistory: IOrderCart, currencySymbol: string) => {
    const total = orderHistory?.total || 0;

    const result = ((total * Number(amountSurchargeFee)) / 100).toFixed(2);
    return `${currencySymbol} ${result}`;
  };

  const handleLogError = (error: any, functionName: string) => {
    const errorData: IHandleErrorProps = {
      logErrorData: {
        endpoint: '',
        menuId: info?.menuId,
        orderNumber: '',
        tableId: info?.tableId,
        error,
        fileName: 'OrderHistory',
        functionName,
      },
      eventTracker,
    };
    handleError(errorData);
  };

  const getTotalAmount = () => {
    try {
      if (hasLength(orderHistoryItemsOnTime)) {
        const result = [...orderHistoryItemsOnTime].reduce((sum, item) => {
          const amountItem = item.order.total || 0;
          return sum + amountItem;
        }, 0);
        return result.toFixed(2);
      } else {
        return null;
      }
    } catch (error: any) {
      handleLogError(error, 'getTotalAmount');
      return null;
    }
  };

  const getTotalHolidaySurcharge = () => {
    try {
      if (hasLength(orderHistoryItemsOnTime)) {
        const result = [...orderHistoryItemsOnTime].reduce((sum, item) => {
          const amountItem = item.order.total || 0;
          const surCharge = (amountItem * Number(amountSurchargeFee)) / 100;
          return sum + surCharge;
        }, 0);
        return result.toFixed(2);
      } else {
        return null;
      }
    } catch (error: any) {
      handleLogError(error, 'getTotalHolidaySurcharge');
      return null;
    }
  };

  useEffect(() => {
    eventTracker(
      EEventCategory.CHECK_ORDER_HISTORY_EVENT,
      EEventAction.CHECK,
      window?.location?.href || '',
    );
  }, [eventTracker]);

  useEffect(() => {
    updateUrlHomeStore({ state, setUrlHome, menuId, tableId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OrderHistoryPage>
      <Header>
        <BoxWrapperIconBack onClick={back}>
          <IconBack name="leftArrow"></IconBack>
        </BoxWrapperIconBack>
      </Header>
      {isNewOrder && newOrder && (
        <Content>
          <InfoRow>
            <Label>{t('Order No')}</Label>
            <Info>{newOrder.reference?.substr(newOrder.reference.length - 4).toUpperCase()}</Info>
            <TableName>
              <TextTable>{renderTableId(newOrder.tableId, isDineInTable)}</TextTable>
            </TableName>
          </InfoRow>
          <InfoRow>
            <Label>{t('Order time')}</Label>
            <Info>{newOrder.order?.orderTime}</Info>
          </InfoRow>

          <CartContent>
            {newOrder.order?.items.map((item: IItemCart, index: number) => (
              <CartItem
                hasRemove={false}
                onRemoveItem={onRemoveItem}
                key={index}
                cartItem={item}
                index={index}
                restaurant={restaurant}
              />
            ))}

            {!!newOrder.order?.total && (
              <SubTotal>
                <TextNormal>{t('Subtotal')}</TextNormal>
                <TextPrice>
                  {restaurant && restaurant.currencySymbol ? restaurant.currencySymbol : '$'}{' '}
                  {newOrder.order?.total}
                </TextPrice>
              </SubTotal>
            )}
            {isHolidaySurcharge(restaurant) && (
              <Box display="flex">
                <TextRed>
                  {restaurant?.holidaySurcharge?.name
                    ? restaurant?.holidaySurcharge?.name
                    : t('Holiday Surcharge Fee')}
                </TextRed>
                <TextFee>
                  {renderSurchargeFee(
                    newOrder.order,
                    restaurant?.currencySymbol ? restaurant?.currencySymbol : '$',
                  )}
                </TextFee>
              </Box>
            )}
          </CartContent>

          <QRBox>
            <QRCode id="qr-gen" value={newOrder?.reference || ''} size={160} />
          </QRBox>
        </Content>
      )}
      {!isNewOrder &&
        orderHistoryItemsOnTime?.map(({ order, reference, tableId }) => {
          if (order) {
            return (
              <Content key={reference}>
                <InfoRow>
                  <Label>{t('Order No')}</Label>
                  <Info>
                    : {newOrder.reference?.substr(newOrder.reference.length - 4).toUpperCase()}
                  </Info>
                  <TableName>
                    <TextTable>{renderTableId(tableId, isDineInTable)}</TextTable>
                  </TableName>
                </InfoRow>
                <InfoRow>
                  <Label>{t('Order time')}</Label>
                  <Info>{order?.orderTime}</Info>
                </InfoRow>

                <CartContent>
                  {order?.items.map((item: IItemCart, index: number) => (
                    <CartItem
                      hasRemove={false}
                      onRemoveItem={onRemoveItem}
                      key={index}
                      cartItem={item}
                      index={index}
                      restaurant={restaurant}
                    />
                  ))}

                  {!!order?.total && (
                    <SubTotal>
                      <TextNormal>{t('Subtotal')}</TextNormal>
                      <TextPrice>
                        {restaurant && restaurant.currencySymbol ? restaurant.currencySymbol : '$'}{' '}
                        {order?.total}
                      </TextPrice>
                    </SubTotal>
                  )}
                  {isHolidaySurcharge(restaurant) && (
                    <Box display="flex">
                      <TextRed>
                        {restaurant?.holidaySurcharge?.name
                          ? restaurant?.holidaySurcharge?.name
                          : t('Holiday Surcharge Fee')}
                      </TextRed>
                      <TextFee>
                        {renderSurchargeFee(
                          order,
                          restaurant && restaurant.currencySymbol ? restaurant.currencySymbol : '$',
                        )}
                      </TextFee>
                    </Box>
                  )}
                </CartContent>

                <QRBox>
                  <QRCode id="qr-gen" value={reference} size={160} />
                </QRBox>
              </Content>
            );
          }
        })}
      <Box style={{ height: '150px' }}></Box>
      {!hasLength(orderHistoryItemsOnTime) ? (
        <TextNoOrder>{t('No orders yet')}</TextNoOrder>
      ) : !isNewOrder ? (
        <Box
          style={{
            width: '100%',
            position: 'fixed',
            bottom: '0',
            left: 0,
            backgroundColor: theme.palette.grey[500],
          }}
        >
          <Box style={{ padding: '20px' }}>
            <Box style={{ display: 'flex' }}>
              <Typography
                style={{ fontSize: '24px', fontWeight: 700, color: theme.palette.grey[900] }}
              >
                {t('Total Amount: ')}
              </Typography>
              <TextPriceLarge>
                {restaurant && restaurant.currencySymbol ? restaurant.currencySymbol : '$'}{' '}
                {getTotalAmount()}
              </TextPriceLarge>
            </Box>
            {amountSurchargeFee ? (
              <Box display="flex">
                <TextRedLarge>
                  {' '}
                  {restaurant?.holidaySurcharge?.name
                    ? restaurant?.holidaySurcharge?.name
                    : t('Holiday Surcharge Fee')}
                </TextRedLarge>
                <TextFeeLarge>$ {getTotalHolidaySurcharge()}</TextFeeLarge>
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </OrderHistoryPage>
  );
};

export default OrderHistory;
